import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../lib/auth";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import ScheduleCalendar from "../components/ScheduleCalendar";
import { toast } from "sonner";
import { useConfirm } from "../components/ConfirmDialog";
import AvatarUploader from "../components/AvatarUploader";
import { SESSION_PACKAGES, sessionPackageLabel } from "../lib/sessionPackages";
import BrandMark from "../components/BrandMark";
import { LayoutDashboard, Users, FileText, Dumbbell, Calendar, Inbox, LogOut, Activity, Clock, ChevronRight, Search, Plus, Edit, Trash2, Link2, Unlink, Save, X, Package, UserPlus, Copy, ArrowRight, Star, EyeOff, KeyRound, ScrollText } from "lucide-react";

// Status badge for the client roster. Three explicit states map to specific
// colors so the table is scannable at a glance: Lead = brand blue (a prospect
// in the pipeline), Active = green (engaged client), Complete = muted gray
// (relationship concluded but not deleted).
function ClientStatusBadge({ status }) {
  const s = (status || "active").toLowerCase();
  const styles = {
    lead:     "bg-[#007AFF]/15 text-[#007AFF] border border-[#007AFF]/30",
    active:   "bg-[#00CC52]/15 text-[#00CC52] border border-[#00CC52]/30",
    complete: "bg-muted text-muted-foreground border border-border",
  };
  const labels = { lead: "Lead", active: "Active", complete: "Complete" };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${styles[s] || styles.active}`}
      data-testid={`client-status-badge-${s}`}
    >
      {labels[s] || s}
    </span>
  );
}


const adminTabs = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "clients", label: "Clients", icon: Users },
  { id: "stories", label: "Stories", icon: Star },
  { id: "activity", label: "Activity", icon: ScrollText },
  // Schedule tab hidden — Otto schedules through Calendly, which lives outside
  // the app. The /api/sessions endpoint and AdminScheduleManager component
  // remain in the codebase for a future re-enable. Same for Packages.
  // { id: "schedule", label: "Schedule", icon: Calendar },
  // { id: "packages", label: "Packages", icon: Package },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showPwForm, setShowPwForm] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [pkgs, setPkgs] = useState([]);
  const [stories, setStories] = useState([]);
  const [searchParams] = useSearchParams();
  const [showAddClient, setShowAddClient] = useState(false);
  const [addClientForm, setAddClientForm] = useState({ name: "", email: "", phone: "", package: "none", client_type: "adult_recovery", goals: "", client_status: "lead", avatar: "" });
  const [addClientResult, setAddClientResult] = useState(null);
  const [showAddLead, setShowAddLead] = useState(false);
  const [addLeadForm, setAddLeadForm] = useState({ name: "", email: "", phone: "", client_type: "general", message: "" });
  const [convertLead, setConvertLead] = useState(null);
  const [convertPackage, setConvertPackage] = useState("limited");
  const [convertResult, setConvertResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchDashboard = useCallback(async () => {
    try {
      const { data: d } = await api.get("/dashboard/admin");
      setData(d);
      setClients(d.clients || []);
    } catch (e) { console.error(e); }
  }, []);

  const fetchCalendarStatus = useCallback(async () => {
    try {
      const { data: cs } = await api.get("/oauth/calendar/status");
      setCalendarStatus(cs);
    } catch { setCalendarStatus({ connected: false }); }
  }, []);

  useEffect(() => { fetchDashboard(); fetchCalendarStatus(); }, [fetchDashboard, fetchCalendarStatus]);

  useEffect(() => {
    if (searchParams.get("calendar") === "connected") fetchCalendarStatus();
  }, [searchParams, fetchCalendarStatus]);

  useEffect(() => {
    // Guard against stale responses if the user switches tabs rapidly.
    let cancelled = false;
    const load = async () => {
      try {
        if (activeTab === "programs") { const { data: p } = await api.get("/programs"); if (!cancelled) setPrograms(p); }
        if (activeTab === "exercises") { const { data: e } = await api.get("/exercises"); if (!cancelled) setExercises(e); }
        if (activeTab === "schedule") { const { data: s } = await api.get("/sessions"); if (!cancelled) setSessions(s); }
        if (activeTab === "consultations") { const { data: c } = await api.get("/consultations"); if (!cancelled) setConsultations(c); }
        if (activeTab === "packages") { const { data: p } = await api.get("/packages"); if (!cancelled) setPkgs(p); }
        if (activeTab === "stories") {
          const { data: st } = await api.get("/stories?active_only=false");
          if (!cancelled) setStories(st);
          // Also refresh clients so the "use existing client" picker works
          // even if the admin lands on Stories before visiting Overview.
          const { data: cl } = await api.get("/clients");
          if (!cancelled) setClients(cl);
        }
      } catch (e) { console.error("Admin tab load failed", e); }
    };
    load();
    return () => { cancelled = true; };
    // State setters are guaranteed stable by React; `api` is a module singleton.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const updateConsultStatus = async (id, status) => {
    await api.put(`/consultations/${id}`, { status });
    const { data: c } = await api.get("/consultations");
    setConsultations(c);
  };

  const submitAddClient = async () => {
    if (!addClientForm.name || !addClientForm.email) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      const { data: result } = await api.post("/clients", { ...addClientForm, password: "" });
      setAddClientResult(result);
      setAddClientForm({ name: "", email: "", phone: "", package: "none", client_type: "adult_recovery", goals: "", client_status: "lead", avatar: "" });
      await fetchDashboard();
      toast.success("Client created & welcome email sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create client");
    }
    setBusy(false);
  };

  const submitAddLead = async () => {
    if (!addLeadForm.name || !addLeadForm.email) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      await api.post("/consultations", addLeadForm);
      setAddLeadForm({ name: "", email: "", phone: "", client_type: "general", message: "" });
      setShowAddLead(false);
      const { data: c } = await api.get("/consultations");
      setConsultations(c);
      toast.success("Lead added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add lead");
    }
    setBusy(false);
  };

  const submitConvert = async () => {
    if (!convertLead) return;
    setBusy(true);
    try {
      const { data: result } = await api.post(`/consultations/${convertLead.id}/convert`, { package: convertPackage });
      setConvertResult(result);
      const { data: c } = await api.get("/consultations");
      setConsultations(c);
      await fetchDashboard();
      toast.success("Lead converted to client");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to convert lead");
    }
    setBusy(false);
  };

  const copyToClipboard = (txt) => {
    try { navigator.clipboard.writeText(txt); toast.success("Copied"); }
    catch (err) { console.warn("Clipboard copy failed", err); toast.error("Copy failed — select and copy manually"); }
  };

  const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }); } catch { return d; } };

  if (!data) return <div className="min-h-screen flex items-center justify-center bg-background"><div className="w-8 h-8 border-2 border-[#007AFF] border-t-transparent rounded-full animate-spin" /></div>;

  const filteredClients = clients.filter(c => c.name?.toLowerCase().includes(searchQ.toLowerCase()) || c.email?.toLowerCase().includes(searchQ.toLowerCase()));

  const renderContent = () => {
    switch (activeTab) {
      case "overview": return (
        <div className="space-y-6" data-testid="admin-overview">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <OvCard label="Total Clients" value={data.total_clients} />
            <OvCard label="Active Programs" value={data.active_programs} />
            <OvCard label="New Leads" value={data.new_leads || 0} accent />
          </div>
          {/* Leads — full width since scheduling lives outside the app (Calendly). */}
          <div className="bg-card border border-border p-6" data-testid="admin-leads-section">
            <h3 className="font-bold mb-4">Leads</h3>
            {(data.leads || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No leads yet. Add a client with status "Lead" to track them here.</p>
            ) : (
              <div className="divide-y divide-border">
                {data.leads.map((c) => (
                  <div key={c.id}
                    onClick={() => navigate(`/admin/clients/${c.id}`)}
                    className="flex items-center justify-between py-3 cursor-pointer hover:bg-secondary/40 -mx-2 px-2 transition-colors"
                    data-testid={`admin-lead-row-${c.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      {c.avatar ? <img src={c.avatar} alt="" className="w-9 h-9 rounded-full object-cover flex-shrink-0" /> : <div className="w-9 h-9 bg-[#007AFF] rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{c.name?.charAt(0)}</div>}
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{c.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
                      </div>
                    </div>
                    <Badge className="bg-[#007AFF] text-white flex-shrink-0">Lead</Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Active Clients — full-width grid, more breathing room now that the
              side column was removed. */}
          <div className="bg-card border border-border p-6">
            <h3 className="font-bold mb-4">Active Clients</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {clients.filter(c => (c.client_status || "active").toLowerCase() === "active").map(c => (
                <div key={c.id} className="border border-border p-4 flex items-center gap-3 cursor-pointer hover:border-[#007AFF]/30 transition-colors" onClick={() => navigate(`/admin/clients/${c.id}`)}>
                  {c.avatar ? <img src={c.avatar} alt="" className="w-10 h-10 rounded-full object-cover" /> : <div className="w-10 h-10 bg-[#007AFF] rounded-full flex items-center justify-center text-white text-sm font-bold">{c.name?.charAt(0)}</div>}
                  <div className="min-w-0">
                    <p className="text-sm font-bold truncate">{c.name}</p>
                    <p className="text-xs text-[#007AFF] font-bold uppercase truncate">{sessionPackageLabel(c.package)}</p>
                    <p className="text-xs text-muted-foreground truncate">{c.goals}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Google Calendar block intentionally removed — Otto uses Calendly
              for all scheduling. The /api/oauth/calendar/* endpoints remain in
              the backend for a future re-enable but are no longer surfaced. */}
        </div>
      );
      case "clients": return (
        <div className="space-y-4" data-testid="admin-clients">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={searchQ} onChange={(e) => setSearchQ(e.target.value)} placeholder="Search clients..." className="pl-9 rounded-none bg-card border-border" data-testid="admin-client-search" />
            </div>
            <Button onClick={() => { setAddClientResult(null); setShowAddClient(true); }} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs font-bold" data-testid="admin-add-client-btn">
              <UserPlus className="w-3.5 h-3.5 mr-1.5" /> Add Client
            </Button>
          </div>
          <div className="bg-card border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-border bg-secondary/50">
                <th className="text-left p-3 font-bold text-xs">CLIENT</th>
                <th className="text-left p-3 font-bold text-xs">STATUS</th>
                <th className="text-left p-3 font-bold text-xs hidden md:table-cell">PACKAGE</th>
                <th className="text-left p-3 font-bold text-xs hidden md:table-cell">TYPE</th>
                <th className="text-left p-3 font-bold text-xs hidden lg:table-cell">GOALS</th>
                <th className="text-left p-3 font-bold text-xs">CONTACT</th>
              </tr></thead>
              <tbody>
                {filteredClients.map(c => (
                  <tr key={c.id} className="border-b border-border hover:bg-secondary/30 transition-colors cursor-pointer" onClick={() => navigate(`/admin/clients/${c.id}`)}>
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        {c.avatar ? <img src={c.avatar} alt="" className="w-8 h-8 rounded-full object-cover" /> : <div className="w-8 h-8 bg-[#007AFF] rounded-full flex items-center justify-center text-white text-xs font-bold">{c.name?.charAt(0)}</div>}
                        <div><p className="font-medium">{c.name}</p><p className="text-xs text-muted-foreground">{c.email}</p></div>
                      </div>
                    </td>
                    <td className="p-3"><ClientStatusBadge status={c.client_status} /></td>
                    <td className="p-3 hidden md:table-cell"><Badge className="bg-[#007AFF]/10 text-[#007AFF] border-0 font-bold uppercase text-[10px]">{sessionPackageLabel(c.package)}</Badge></td>
                    <td className="p-3 hidden md:table-cell capitalize text-muted-foreground text-xs">{c.client_type?.replace("_", " ")}</td>
                    <td className="p-3 hidden lg:table-cell text-muted-foreground text-xs max-w-xs truncate">{c.goals}</td>
                    <td className="p-3 text-xs text-muted-foreground">{c.phone}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
      case "programs": return (
        <div className="space-y-4" data-testid="admin-programs">
          <h3 className="font-bold text-lg">Active Programs</h3>
          <div className="space-y-3">
            {programs.map(p => {
              const client = clients.find(c => c.id === p.client_id);
              return (
                <div key={p.id} className="bg-card border border-border p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h4 className="font-bold">{p.title}</h4>
                      <p className="text-sm text-[#007AFF] font-medium">{client?.name || "Unknown client"}</p>
                    </div>
                    <Badge className={p.status === "in_progress" ? "bg-[#007AFF]/10 text-[#007AFF] border-0" : ""}>{p.status?.replace("_", " ")}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">{p.objective}</p>
                  <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                    <span>Start: {p.start_date}</span><span>End: {p.end_date}</span><span>{p.duration_weeks} weeks</span>
                    {p.mid_check_in?.completed && <span className="text-[#00CC52]">Mid-check complete</span>}
                  </div>
                  {p.baseline_assessment && <div className="mt-3 pt-3 border-t border-border"><p className="text-xs text-muted-foreground"><span className="font-bold">Baseline:</span> {p.baseline_assessment}</p></div>}
                </div>
              );
            })}
          </div>
        </div>
      );
      case "exercises": return <ExerciseLibrary exercises={exercises} onReload={async () => { const { data: e } = await api.get("/exercises"); setExercises(e); }} />;
      case "schedule": {
        const enriched = sessions.map(s => ({ ...s, client_name: clients.find(c => c.id === s.client_id)?.name || "Unknown" }));
        return <AdminScheduleTab sessions={sessions} enrichedSessions={enriched} clients={clients} fmtDate={fmtDate} onReload={async () => { const { data: s } = await api.get("/sessions"); setSessions(s); }} />;
      }
      case "packages": return <PackagesManager pkgs={pkgs} onReload={async () => { const { data: p } = await api.get("/packages"); setPkgs(p); }} />;
      case "stories": return <StoriesManager stories={stories} clients={clients} onReload={async () => { const { data: st } = await api.get("/stories?active_only=false"); setStories(st); }} />;
      case "activity": return <AuditLogManager />;
      case "consultations": return (
        <div className="space-y-4" data-testid="admin-consultations">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-lg">Consultation Leads ({consultations.length})</h3>
            <Button onClick={() => setShowAddLead(true)} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs font-bold" data-testid="admin-add-lead-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Lead
            </Button>
          </div>
          <div className="space-y-3">
            {consultations.length === 0 && <p className="text-sm text-muted-foreground">No leads yet.</p>}
            {consultations.map(c => (
              <div key={c.id} className="bg-card border border-border p-5" data-testid={`lead-row-${c.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-bold">{c.name}</h4>
                    <p className="text-xs text-muted-foreground">{c.email} | {c.phone}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={c.status === "new" ? "bg-[#007AFF] text-white" : c.status === "contacted" ? "bg-yellow-500/20 text-yellow-500 border-0" : c.status === "converted" ? "bg-[#00CC52]/10 text-[#00CC52] border-0" : ""}>{c.status}</Badge>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-3">{c.message}</p>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>Type: {c.client_type}</span>
                  <span>Submitted: {fmtDate(c.created_at)}</span>
                </div>
                <div className="mt-3 pt-3 border-t border-border flex gap-2 flex-wrap">
                  {c.status === "new" && <Button size="sm" onClick={() => updateConsultStatus(c.id, "contacted")} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs" data-testid={`consult-contact-${c.id}`}>Mark Contacted</Button>}
                  {c.status === "contacted" && <Button size="sm" onClick={() => updateConsultStatus(c.id, "scheduled")} className="bg-[#00CC52] hover:bg-[#00CC52]/80 text-white rounded-none text-xs">Mark Scheduled</Button>}
                  {c.status === "scheduled" && <Button size="sm" onClick={() => updateConsultStatus(c.id, "completed")} variant="outline" className="rounded-none text-xs">Mark Completed</Button>}
                  {c.status !== "converted" && (
                    <Button size="sm" onClick={() => { setConvertResult(null); setConvertLead(c); setConvertPackage("limited"); }} className="bg-[#00CC52] hover:bg-[#00CC52]/80 text-white rounded-none text-xs ml-auto" data-testid={`consult-convert-${c.id}`}>
                      <ArrowRight className="w-3 h-3 mr-1" /> Convert to Client
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
      default: return null;
    }
  };

  return (
    <div className="flex h-screen bg-background text-foreground" data-testid="admin-dashboard">
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-card border-r border-border flex flex-col transform transition-transform lg:translate-x-0 lg:static ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-5 border-b border-border flex items-center justify-between gap-2">
          <BrandMark className="h-6" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Admin</span>
        </div>
        <div className="p-4 border-b border-border">
          <div className="flex items-center gap-3">
            {user?.avatar ? <img src={user.avatar} alt="" className="w-9 h-9 rounded-full object-cover" /> : <div className="w-9 h-9 bg-[#007AFF] rounded-full flex items-center justify-center text-white text-sm font-bold">O</div>}
            <div><p className="text-sm font-bold">{user?.name}</p><p className="text-[10px] text-[#007AFF] font-bold uppercase">Administrator</p></div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {adminTabs.map(t => (
            <button key={t.id} onClick={() => { setActiveTab(t.id); setSidebarOpen(false); }}
              className={`w-full text-left px-3 py-2.5 flex items-center gap-3 text-sm transition-colors ${activeTab === t.id ? "bg-[#007AFF] text-white font-bold" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}
              data-testid={`admin-tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
              {t.id === "consultations" && data?.new_consultations > 0 && <span className="ml-auto bg-[#007AFF] text-white text-[10px] px-1.5 py-0.5 rounded-full">{data.new_consultations}</span>}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-border space-y-1">
          <button onClick={() => setShowPwForm(true)} className="w-full text-left px-3 py-2.5 flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="admin-change-password-btn">
            <KeyRound className="w-4 h-4" /> Change Password
          </button>
          <button onClick={logout} className="w-full text-left px-3 py-2.5 flex items-center gap-3 text-sm text-muted-foreground hover:text-foreground" data-testid="admin-logout">
            <LogOut className="w-4 h-4" /> Log Out
          </button>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <main className="flex-1 overflow-y-auto min-w-0">
        <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-xl border-b border-border px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-1"><ChevronRight className="w-5 h-5" /></button>
            <h1 className="text-lg font-bold">{adminTabs.find(t => t.id === activeTab)?.label}</h1>
          </div>
        </header>
        <div className="p-6">{renderContent()}</div>
      </main>

      <ChangePasswordDialog open={showPwForm} onClose={() => setShowPwForm(false)} />

      {/* Add Client Dialog */}
      <Dialog open={showAddClient} onOpenChange={(o) => { setShowAddClient(o); if (!o) setAddClientResult(null); }}>
        <DialogContent className="max-w-lg rounded-none bg-card border-border" data-testid="add-client-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-black tracking-tight">{addClientResult ? "Client Created" : "Add New Client"}</DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">
              {addClientResult ? "The client has been added." : "Add a new client manually. The client will not receive any login or email at this time."}
            </DialogDescription>
          </DialogHeader>
          {addClientResult ? (
            <div className="space-y-3">
              <div className="bg-[#00CC52]/10 border border-[#00CC52]/30 p-4 space-y-2 text-sm">
                <p><span className="text-muted-foreground">Name:</span> <span className="font-bold">{addClientResult.name}</span></p>
                <p><span className="text-muted-foreground">Email:</span> <span className="font-bold">{addClientResult.email}</span></p>
                <p><span className="text-muted-foreground">Status:</span> <span className="font-bold uppercase">{addClientResult.client_status || "lead"}</span></p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Photo (optional)</Label>
                <AvatarUploader
                  value={addClientForm.avatar}
                  onChange={(dataUrl) => setAddClientForm({ ...addClientForm, avatar: dataUrl })}
                  fallbackName={addClientForm.name}
                  testIdPrefix="add-client-avatar"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Full Name *</Label><Input value={addClientForm.name} onChange={e => setAddClientForm({...addClientForm, name: e.target.value})} className="mt-1 rounded-none bg-background border-border text-sm" data-testid="add-client-name" /></div>
                <div><Label className="text-xs">Email *</Label><Input type="email" value={addClientForm.email} onChange={e => setAddClientForm({...addClientForm, email: e.target.value})} className="mt-1 rounded-none bg-background border-border text-sm" data-testid="add-client-email" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Phone</Label><Input value={addClientForm.phone} onChange={e => setAddClientForm({...addClientForm, phone: e.target.value})} className="mt-1 rounded-none bg-background border-border text-sm" data-testid="add-client-phone" /></div>
                <div><Label className="text-xs">Package</Label>
                  <select value={addClientForm.package} onChange={e => setAddClientForm({...addClientForm, package: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border border-border rounded-none" data-testid="add-client-package">
                    {SESSION_PACKAGES.map((p) => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Client Type</Label>
                <select value={addClientForm.client_type} onChange={e => setAddClientForm({...addClientForm, client_type: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border border-border rounded-none" data-testid="add-client-type">
                  <option value="elite_athlete">Elite Athlete</option>
                  <option value="adult_recovery">Adult / Recovery</option>
                  <option value="youth_athlete">Youth Athlete</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <select value={addClientForm.client_status} onChange={e => setAddClientForm({...addClientForm, client_status: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border border-border rounded-none" data-testid="add-client-status">
                  <option value="lead">Lead</option>
                  <option value="active">Active</option>
                  <option value="complete">Complete</option>
                </select>
              </div>
              <div><Label className="text-xs">Goals</Label><Textarea value={addClientForm.goals} onChange={e => setAddClientForm({...addClientForm, goals: e.target.value})} rows={2} className="mt-1 rounded-none bg-background border-border text-sm resize-none" data-testid="add-client-goals" /></div>
            </div>
          )}
          <DialogFooter className="gap-2">
            {addClientResult ? (
              <Button onClick={() => { setShowAddClient(false); setAddClientResult(null); }} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none font-bold" data-testid="add-client-close">Done</Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setShowAddClient(false)} className="rounded-none">Cancel</Button>
                <Button onClick={submitAddClient} disabled={busy} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none font-bold" data-testid="add-client-submit">
                  {busy ? "Creating..." : "Create Client"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Lead Dialog */}
      <Dialog open={showAddLead} onOpenChange={setShowAddLead}>
        <DialogContent className="max-w-lg rounded-none bg-card border-border" data-testid="add-lead-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-black tracking-tight">Add New Lead</DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">Manually enter a lead from a call, email, or referral.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Full Name *</Label><Input value={addLeadForm.name} onChange={e => setAddLeadForm({...addLeadForm, name: e.target.value})} className="mt-1 rounded-none bg-background border-border text-sm" data-testid="add-lead-name" /></div>
              <div><Label className="text-xs">Email *</Label><Input type="email" value={addLeadForm.email} onChange={e => setAddLeadForm({...addLeadForm, email: e.target.value})} className="mt-1 rounded-none bg-background border-border text-sm" data-testid="add-lead-email" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Phone</Label><Input value={addLeadForm.phone} onChange={e => setAddLeadForm({...addLeadForm, phone: e.target.value})} className="mt-1 rounded-none bg-background border-border text-sm" data-testid="add-lead-phone" /></div>
              <div><Label className="text-xs">Client Type</Label>
                <select value={addLeadForm.client_type} onChange={e => setAddLeadForm({...addLeadForm, client_type: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border border-border rounded-none" data-testid="add-lead-type">
                  <option value="general">General</option>
                  <option value="athlete">Elite Athlete</option>
                  <option value="recovery">Adult / Recovery</option>
                  <option value="youth">Youth Athlete</option>
                </select>
              </div>
            </div>
            <div><Label className="text-xs">Notes / Message</Label><Textarea value={addLeadForm.message} onChange={e => setAddLeadForm({...addLeadForm, message: e.target.value})} rows={3} className="mt-1 rounded-none bg-background border-border text-sm resize-none" data-testid="add-lead-message" /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setShowAddLead(false)} className="rounded-none">Cancel</Button>
            <Button onClick={submitAddLead} disabled={busy} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none font-bold" data-testid="add-lead-submit">
              {busy ? "Adding..." : "Add Lead"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert Lead Dialog */}
      <Dialog open={!!convertLead} onOpenChange={(o) => { if (!o) { setConvertLead(null); setConvertResult(null); } }}>
        <DialogContent className="max-w-lg rounded-none bg-card border-border" data-testid="convert-lead-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-black tracking-tight">{convertResult ? "Client Created" : "Convert Lead to Client"}</DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">
              {convertResult ? "The lead has been converted." : `Convert ${convertLead?.name} into an active client.`}
            </DialogDescription>
          </DialogHeader>
          {convertResult ? (
            <div className="bg-[#00CC52]/10 border border-[#00CC52]/30 p-4 space-y-2 text-sm">
              <p><span className="text-muted-foreground">Name:</span> <span className="font-bold">{convertResult.name}</span></p>
              <p><span className="text-muted-foreground">Email:</span> <span className="font-bold">{convertResult.email}</span></p>
              <p><span className="text-muted-foreground">Status:</span> <span className="font-bold uppercase">{convertResult.client_status || "active"}</span></p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-secondary/50 p-3 text-xs text-muted-foreground">
                <p><span className="font-medium text-foreground">{convertLead?.name}</span> · {convertLead?.email}</p>
                {convertLead?.message && <p className="mt-1">{convertLead.message}</p>}
              </div>
              <div>
                <Label className="text-xs">Assign Package</Label>
                <select value={convertPackage} onChange={e => setConvertPackage(e.target.value)} className="w-full mt-1 px-3 py-2 text-sm bg-background border border-border rounded-none" data-testid="convert-lead-package">
                  {SESSION_PACKAGES.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            {convertResult ? (
              <Button onClick={() => { setConvertLead(null); setConvertResult(null); }} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none font-bold" data-testid="convert-lead-close">Done</Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => setConvertLead(null)} className="rounded-none">Cancel</Button>
                <Button onClick={submitConvert} disabled={busy} className="bg-[#00CC52] hover:bg-[#00CC52]/80 text-white rounded-none font-bold" data-testid="convert-lead-submit">
                  {busy ? "Converting..." : "Convert to Client"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PackagesManager({ pkgs, onReload }) {
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: "", price: "", period: "/mo", best_for: "", features: "", highlight: false, badge: "", badge_color: "#007AFF", cta_text: "Get Started", cta_color: "", border_style: "solid", booking_id: "", sort_order: 99, active: true });

  const resetForm = () => setForm({ name: "", price: "", period: "/mo", best_for: "", features: "", highlight: false, badge: "", badge_color: "#007AFF", cta_text: "Get Started", cta_color: "", border_style: "solid", booking_id: "", sort_order: 99, active: true });

  const startEdit = (p) => {
    setForm({ name: p.name || "", price: p.price || "", period: p.period || "/mo", best_for: p.best_for || "", features: (p.features || []).join("\n"), highlight: p.highlight || false, badge: p.badge || "", badge_color: p.badge_color || "#007AFF", cta_text: p.cta_text || "Get Started", cta_color: p.cta_color || "", border_style: p.border_style || "solid", booking_id: p.booking_id || "", sort_order: p.sort_order || 99, active: p.active !== false });
    setEditId(p.id); setShowForm(true);
  };

  const save = async () => {
    const payload = { ...form, features: form.features.split("\n").map(s => s.trim()).filter(Boolean) };
    if (editId) { await api.put(`/packages/${editId}`, payload); }
    else { await api.post("/packages", payload); }
    setShowForm(false); setEditId(null); resetForm(); onReload();
  };

  const del = async (id) => {
    const p = pkgs.find(x => x.id === id);
    const ok = await confirm({ title: "Delete package?", description: `"${p?.name || "This package"}" will be removed from the website and booking page. Existing client assignments are not affected.`, confirmText: "Delete package" });
    if (!ok) return;
    await api.delete(`/packages/${id}`); onReload();
  };

  if (showForm) {
    return (
      <div className="space-y-5" data-testid="package-form">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg">{editId ? "Edit Package" : "New Package"}</h3>
          <Button size="sm" variant="ghost" onClick={() => { setShowForm(false); setEditId(null); resetForm(); }} className="rounded-none text-xs"><X className="w-3 h-3 mr-1" /> Cancel</Button>
        </div>
        <div className="bg-card border border-border p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Package Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="mt-1 rounded-none bg-background border-border text-sm" data-testid="pkg-form-name" /></div>
            <div><Label className="text-xs">Price *</Label><Input value={form.price} onChange={e => setForm({...form, price: e.target.value})} placeholder="$899" className="mt-1 rounded-none bg-background border-border text-sm" data-testid="pkg-form-price" /></div>
            <div><Label className="text-xs">Period</Label><Input value={form.period} onChange={e => setForm({...form, period: e.target.value})} placeholder="/mo or one-time" className="mt-1 rounded-none bg-background border-border text-sm" /></div>
          </div>
          <div><Label className="text-xs">Best For (description)</Label><Textarea value={form.best_for} onChange={e => setForm({...form, best_for: e.target.value})} rows={2} className="mt-1 rounded-none bg-background border-border text-sm resize-none" /></div>
          <div><Label className="text-xs">Features (one per line)</Label><Textarea value={form.features} onChange={e => setForm({...form, features: e.target.value})} rows={5} placeholder="3x/week in-office sessions&#10;Full app access&#10;Priority scheduling" className="mt-1 rounded-none bg-background border-border text-sm resize-none" data-testid="pkg-form-features" /></div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label className="text-xs">Booking ID</Label><Input value={form.booking_id} onChange={e => setForm({...form, booking_id: e.target.value})} placeholder="e.g. elite" className="mt-1 rounded-none bg-background border-border text-sm" /></div>
            <div><Label className="text-xs">Sort Order</Label><Input type="number" value={form.sort_order} onChange={e => setForm({...form, sort_order: parseInt(e.target.value) || 0})} className="mt-1 rounded-none bg-background border-border text-sm" /></div>
            <div><Label className="text-xs">Badge Text</Label><Input value={form.badge} onChange={e => setForm({...form, badge: e.target.value})} placeholder="Most Popular" className="mt-1 rounded-none bg-background border-border text-sm" /></div>
            <div><Label className="text-xs">Badge Color</Label><Input value={form.badge_color} onChange={e => setForm({...form, badge_color: e.target.value})} className="mt-1 rounded-none bg-background border-border text-sm" /></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label className="text-xs">CTA Text</Label><Input value={form.cta_text} onChange={e => setForm({...form, cta_text: e.target.value})} className="mt-1 rounded-none bg-background border-border text-sm" /></div>
            <div><Label className="text-xs">CTA Color (hex)</Label><Input value={form.cta_color} onChange={e => setForm({...form, cta_color: e.target.value})} placeholder="#007AFF" className="mt-1 rounded-none bg-background border-border text-sm" /></div>
            <div><Label className="text-xs">Border Style</Label>
              <select value={form.border_style} onChange={e => setForm({...form, border_style: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border border-border rounded-none">
                <option value="solid">Solid</option><option value="dashed">Dashed</option>
              </select></div>
            <div className="flex flex-col gap-2 pt-5">
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={form.highlight} onChange={e => setForm({...form, highlight: e.target.checked})} /> Highlighted (ring + top bar)</label>
              <label className="flex items-center gap-2 text-xs cursor-pointer"><input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})} /> Active on website</label>
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button onClick={save} disabled={!form.name || !form.price} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none font-bold text-sm px-6" data-testid="pkg-form-save"><Save className="w-4 h-4 mr-2" />{editId ? "Update" : "Create"} Package</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-packages">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg">Packages ({pkgs.length})</h3>
        <Button size="sm" onClick={() => { setEditId(null); resetForm(); setShowForm(true); }} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs" data-testid="add-package-btn"><Plus className="w-3 h-3 mr-1" /> New Package</Button>
      </div>
      <div className="space-y-3">
        {pkgs.map((p, i) => (
          <div key={p.id} className="bg-card border border-border p-5 flex items-start justify-between" data-testid={`pkg-row-${i}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <h4 className="font-bold">{p.name}</h4>
                <span className="text-xl font-black">{p.price}<span className="text-sm text-muted-foreground font-normal">{p.period}</span></span>
                {p.highlight && <Badge className="bg-[#007AFF]/10 text-[#007AFF] border-0 text-[10px]">Highlighted</Badge>}
                {p.badge && <Badge style={{ backgroundColor: `${p.badge_color}20`, color: p.badge_color }} className="border-0 text-[10px]">{p.badge}</Badge>}
                {!p.active && <Badge variant="secondary" className="text-[10px]">Inactive</Badge>}
              </div>
              <p className="text-xs text-muted-foreground mb-2">{p.best_for}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {(p.features || []).map((f, j) => <span key={`${p.id}-feat-${j}`} className="text-xs text-muted-foreground">- {f}</span>)}
              </div>
              <div className="flex gap-4 mt-2 text-[10px] text-muted-foreground">
                <span>Order: {p.sort_order}</span>
                <span>Booking ID: {p.booking_id || "—"}</span>
                <span>CTA: {p.cta_text}</span>
              </div>
            </div>
            <div className="flex gap-1 ml-4 flex-shrink-0">
              <Button size="sm" variant="ghost" onClick={() => startEdit(p)} className="h-7 w-7 p-0" data-testid={`pkg-edit-${i}`}><Edit className="w-3 h-3" /></Button>
              <Button size="sm" variant="ghost" onClick={() => del(p.id)} className="h-7 w-7 p-0 text-destructive" data-testid={`pkg-del-${i}`}><Trash2 className="w-3 h-3" /></Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminScheduleTab({ sessions, enrichedSessions, clients, fmtDate, onReload }) {
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ client_id: "", type: "in_person", date: "", time: "", duration_minutes: 60, notes: "" });

  const createSession = async () => {
    if (!form.client_id || !form.date || !form.time) return;
    await api.post("/sessions", form);
    setForm({ client_id: "", type: "in_person", date: "", time: "", duration_minutes: 60, notes: "" });
    setShowForm(false);
    onReload();
  };

  const delSession = async (id) => {
    const s = sessions.find(x => x.id === id);
    const when = s ? `${s.date} at ${s.time}` : "this session";
    const ok = await confirm({ title: "Cancel session?", description: `Remove the session on ${when}? The client will no longer see it on their schedule.`, confirmText: "Cancel session" });
    if (!ok) return;
    await api.delete(`/sessions/${id}`);
    onReload();
  };

  return (
    <div className="space-y-6" data-testid="admin-schedule">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg">All Sessions</h3>
        <Button size="sm" onClick={() => setShowForm(!showForm)} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs" data-testid="admin-add-session">
          <Plus className="w-3 h-3 mr-1" /> Add Session
        </Button>
      </div>

      {showForm && (
        <div className="bg-card border border-border p-5 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-xs">Client *</Label>
              <select value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value })}
                className="w-full mt-1 px-3 py-2 text-sm bg-background border border-border rounded-none focus:outline-none focus:ring-1 focus:ring-[#007AFF]" data-testid="session-client-select">
                <option value="">Select client...</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Type</Label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}
                className="w-full mt-1 px-3 py-2 text-sm bg-background border border-border rounded-none focus:outline-none focus:ring-1 focus:ring-[#007AFF]" data-testid="session-type-select">
                <option value="in_person">In Person</option>
                <option value="virtual">Virtual</option>
                <option value="evaluation">Evaluation</option>
                <option value="check_in">Check-In</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Date *</Label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })}
                className="mt-1 rounded-none bg-background border-border text-sm" data-testid="session-date-input" />
            </div>
            <div>
              <Label className="text-xs">Time *</Label>
              <Input type="time" value={form.time} onChange={e => setForm({ ...form, time: e.target.value })}
                className="mt-1 rounded-none bg-background border-border text-sm" data-testid="session-time-input" />
            </div>
            <div>
              <Label className="text-xs">Duration (min)</Label>
              <Input type="number" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })}
                className="mt-1 rounded-none bg-background border-border text-sm" />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                className="mt-1 rounded-none bg-background border-border text-sm" data-testid="session-notes-input" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" onClick={createSession} disabled={!form.client_id || !form.date || !form.time}
              className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs disabled:opacity-30" data-testid="session-save-btn">
              <Save className="w-3 h-3 mr-1" /> Create Session
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowForm(false)} className="rounded-none text-xs">Cancel</Button>
          </div>
        </div>
      )}

      <ScheduleCalendar sessions={enrichedSessions} showClient />

      <div className="bg-card border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border bg-secondary/50">
            <th className="text-left p-3 font-bold text-xs">DATE</th>
            <th className="text-left p-3 font-bold text-xs">TIME</th>
            <th className="text-left p-3 font-bold text-xs">CLIENT</th>
            <th className="text-left p-3 font-bold text-xs hidden md:table-cell">TYPE</th>
            <th className="text-left p-3 font-bold text-xs hidden md:table-cell">NOTES</th>
            <th className="text-left p-3 font-bold text-xs">STATUS</th>
            <th className="text-right p-3 font-bold text-xs w-16">ACTIONS</th>
          </tr></thead>
          <tbody>
            {sessions.map(s => {
              const client = clients.find(c => c.id === s.client_id);
              return (
                <tr key={s.id} className="border-b border-border">
                  <td className="p-3 font-medium">{fmtDate(s.date)}</td>
                  <td className="p-3 text-muted-foreground">{s.time}</td>
                  <td className="p-3">{client?.name || "Unknown"}</td>
                  <td className="p-3 hidden md:table-cell capitalize text-muted-foreground">{s.type?.replace("_", " ")}</td>
                  <td className="p-3 hidden md:table-cell text-muted-foreground text-xs max-w-xs truncate">{s.notes}</td>
                  <td className="p-3"><Badge variant={s.status === "scheduled" ? "default" : "secondary"} className={s.status === "scheduled" ? "bg-[#007AFF] text-white text-[10px]" : "text-[10px]"}>{s.status}</Badge></td>
                  <td className="p-3 text-right">
                    {s.status === "scheduled" && (
                      <Button size="sm" variant="ghost" onClick={() => delSession(s.id)} className="h-7 w-7 p-0 text-destructive" data-testid={`session-del-${s.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function OvCard({ label, value, accent = false }) {
  return (
    <div className="bg-card border border-border p-5">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className={`text-3xl font-black ${accent ? "text-[#007AFF]" : ""}`}>{value}</p>
    </div>
  );
}

const EX_CATEGORIES = ["Strength", "Speed", "Mobility", "Stability", "Conditioning", "Rehab"];
const EX_PATTERNS = ["Squat", "Hinge", "Lunge", "Push", "Pull", "Carry", "Rotation", "Gait", "Sprint", "Jump"];
const EX_BODY_AREAS = ["Lower Body", "Upper Body", "Core", "Full Body"];
const EX_EQUIPMENT = ["Bodyweight", "Dumbbell", "Barbell", "Kettlebell", "Band", "Cable", "Machine", "Medicine Ball", "Sled", "Other"];
const EX_POSITIONS = ["Standing", "Seated", "Half-Kneeling", "Tall-Kneeling", "Supine", "Prone", "Quadruped"];
const EX_LATERALITY = ["Bilateral", "Unilateral", "Alternating"];
const EX_PURPOSE = ["Strength", "Power", "Mobility", "Stability", "Endurance", "Technique"];
const EX_DIFFICULTY = ["Beginner", "Intermediate", "Advanced"];

const toVal = (s) => s.toLowerCase().replace(/[\s-]+/g, "_");

function ExerciseLibrary({ exercises, onReload }) {
  const confirm = useConfirm();
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [filterPattern, setFilterPattern] = useState("");
  const [filterBody, setFilterBody] = useState("");
  const [filterEquip, setFilterEquip] = useState("");
  const [form, setForm] = useState({ title: "", description: "", category: "strength", movement_pattern: "", body_region: "full_body", equipment: "bodyweight", body_position: "", laterality: "", primary_purpose: "", difficulty: "intermediate", coaching_cues: "", video_url: "", thumbnail: "", reps: "", sets: "", duration: "", tags: "", notes: "" });

  const resetForm = () => setForm({ title: "", description: "", category: "strength", movement_pattern: "", body_region: "full_body", equipment: "bodyweight", body_position: "", laterality: "", primary_purpose: "", difficulty: "intermediate", coaching_cues: "", video_url: "", thumbnail: "", reps: "", sets: "", duration: "", tags: "", notes: "" });

  const startEdit = (ex) => {
    setForm({ title: ex.title || "", description: ex.description || "", category: ex.category || "strength", movement_pattern: ex.movement_pattern || "", body_region: ex.body_region || "full_body", equipment: ex.equipment || "bodyweight", body_position: ex.body_position || "", laterality: ex.laterality || "", primary_purpose: ex.primary_purpose || "", difficulty: ex.difficulty || "intermediate", coaching_cues: (ex.coaching_cues || []).join("\n"), video_url: ex.video_url || "", thumbnail: ex.thumbnail || "", reps: ex.reps || "", sets: ex.sets || "", duration: ex.duration || "", tags: (ex.tags || []).join(", "), notes: ex.notes || "" });
    setEditId(ex.id); setShowForm(true);
  };

  const save = async () => {
    const payload = { ...form, coaching_cues: form.coaching_cues.split("\n").map(s => s.trim()).filter(Boolean), tags: form.tags.split(",").map(s => s.trim()).filter(Boolean) };
    if (editId) { await api.put(`/exercises/${editId}`, payload); }
    else { await api.post("/exercises", payload); }
    setShowForm(false); setEditId(null); resetForm(); onReload();
  };

  const del = async (id) => {
    const e = exercises.find(x => x.id === id);
    const ok = await confirm({ title: "Delete exercise?", description: `"${e?.title || "This exercise"}" will be removed from the library. Any client programs that reference it will lose the exercise details.`, confirmText: "Delete exercise" });
    if (!ok) return;
    await api.delete(`/exercises/${id}`); onReload();
  };

  const filtered = exercises.filter(ex => {
    if (search && !ex.title?.toLowerCase().includes(search.toLowerCase()) && !(ex.tags || []).some(t => t.toLowerCase().includes(search.toLowerCase()))) return false;
    if (filterCat && ex.category !== filterCat) return false;
    if (filterPattern && ex.movement_pattern !== filterPattern) return false;
    if (filterBody && ex.body_region !== filterBody) return false;
    if (filterEquip && ex.equipment !== filterEquip) return false;
    return true;
  });

  const Dd = ({ label, value, onChange, options, placeholder = "All" }) => (
    <div>
      {label && <Label className="text-[10px] font-bold text-muted-foreground uppercase">{label}</Label>}
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full mt-0.5 px-2 py-1.5 text-xs bg-card border border-border rounded-none focus:outline-none focus:ring-1 focus:ring-[#007AFF]" data-testid={`ex-filter-${(label||"").toLowerCase().replace(/\s/g,"-")}`}>
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o} value={typeof o === "string" ? toVal(o) : o}>{o}</option>)}
      </select>
    </div>
  );

  const FormDd = ({ label, field, options, required = false }) => (
    <div>
      <Label className="text-xs">{label}{required && " *"}</Label>
      <select value={form[field]} onChange={e => setForm({...form, [field]: e.target.value})}
        className="w-full mt-1 px-3 py-2 text-sm bg-card border border-border rounded-none focus:outline-none focus:ring-1 focus:ring-[#007AFF]" data-testid={`ex-form-${field}`}>
        <option value="">Select...</option>
        {options.map(o => <option key={o} value={toVal(o)}>{o}</option>)}
      </select>
    </div>
  );

  if (showForm) {
    return (
      <div className="space-y-5" data-testid="exercise-form">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg">{editId ? "Edit Exercise" : "New Exercise"}</h3>
          <Button size="sm" variant="ghost" onClick={() => { setShowForm(false); setEditId(null); resetForm(); }} className="rounded-none text-xs"><X className="w-3 h-3 mr-1" /> Cancel</Button>
        </div>
        <div className="bg-card border border-border p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Exercise Name *</Label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} className="mt-1 rounded-none bg-background border-border text-sm" data-testid="ex-form-title" /></div>
            <FormDd label="Category" field="category" options={EX_CATEGORIES} required />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <FormDd label="Movement Pattern" field="movement_pattern" options={EX_PATTERNS} />
            <FormDd label="Primary Body Area" field="body_region" options={EX_BODY_AREAS} />
            <FormDd label="Equipment" field="equipment" options={EX_EQUIPMENT} />
            <FormDd label="Body Position" field="body_position" options={EX_POSITIONS} />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <FormDd label="Laterality" field="laterality" options={EX_LATERALITY} />
            <FormDd label="Primary Purpose" field="primary_purpose" options={EX_PURPOSE} />
            <FormDd label="Difficulty Level" field="difficulty" options={EX_DIFFICULTY} required />
            <div><Label className="text-xs">Duration</Label><Input value={form.duration} onChange={e => setForm({...form, duration: e.target.value})} placeholder="e.g. 10 min" className="mt-1 rounded-none bg-background border-border text-sm" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Reps</Label><Input value={form.reps} onChange={e => setForm({...form, reps: e.target.value})} placeholder="e.g. 10 each side" className="mt-1 rounded-none bg-background border-border text-sm" /></div>
            <div><Label className="text-xs">Sets</Label><Input value={form.sets} onChange={e => setForm({...form, sets: e.target.value})} placeholder="e.g. 3" className="mt-1 rounded-none bg-background border-border text-sm" /></div>
            <div><Label className="text-xs">Tags (comma-separated)</Label><Input value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} placeholder="e.g. hip, balance" className="mt-1 rounded-none bg-background border-border text-sm" /></div>
          </div>
          <div><Label className="text-xs">Instructions</Label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={2} className="mt-1 rounded-none bg-background border-border text-sm resize-none" data-testid="ex-form-description" /></div>
          <div><Label className="text-xs">Coaching Cues (one per line)</Label><Textarea value={form.coaching_cues} onChange={e => setForm({...form, coaching_cues: e.target.value})} rows={3} placeholder="Drive knees out&#10;Keep chest up&#10;Squeeze at top" className="mt-1 rounded-none bg-background border-border text-sm resize-none" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Video URL</Label><Input value={form.video_url} onChange={e => setForm({...form, video_url: e.target.value})} placeholder="YouTube or direct link" className="mt-1 rounded-none bg-background border-border text-sm" data-testid="ex-form-video" /></div>
            <div><Label className="text-xs">Thumbnail URL</Label><Input value={form.thumbnail} onChange={e => setForm({...form, thumbnail: e.target.value})} placeholder="Image URL" className="mt-1 rounded-none bg-background border-border text-sm" /></div>
          </div>
          <div><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="mt-1 rounded-none bg-background border-border text-sm" /></div>
          <div className="flex gap-2 justify-end pt-2">
            <Button onClick={save} disabled={!form.title} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none font-bold text-sm px-6" data-testid="ex-form-save"><Save className="w-4 h-4 mr-2" />{editId ? "Update" : "Create"} Exercise</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="admin-exercises">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-lg">Exercise Library ({filtered.length})</h3>
        <Button size="sm" onClick={() => { setEditId(null); resetForm(); setShowForm(true); }} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none text-xs" data-testid="add-exercise-btn"><Plus className="w-3 h-3 mr-1" /> New Exercise</Button>
      </div>
      <div className="bg-card border border-border p-3">
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <div className="sm:col-span-1">
            <Label className="text-[10px] font-bold text-muted-foreground uppercase">Search</Label>
            <div className="relative mt-0.5">
              <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Name or tag..."
                className="w-full pl-7 pr-2 py-1.5 text-xs bg-background border border-border rounded-none focus:outline-none focus:ring-1 focus:ring-[#007AFF]" data-testid="ex-search" />
            </div>
          </div>
          <Dd label="Category" value={filterCat} onChange={setFilterCat} options={EX_CATEGORIES} />
          <Dd label="Movement" value={filterPattern} onChange={setFilterPattern} options={EX_PATTERNS} />
          <Dd label="Body Area" value={filterBody} onChange={setFilterBody} options={EX_BODY_AREAS} />
          <Dd label="Equipment" value={filterEquip} onChange={setFilterEquip} options={EX_EQUIPMENT} />
        </div>
      </div>
      <div className="bg-card border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border bg-secondary/50">
            <th className="text-left p-3 font-bold text-xs">EXERCISE</th>
            <th className="text-left p-3 font-bold text-xs hidden md:table-cell">CATEGORY</th>
            <th className="text-left p-3 font-bold text-xs hidden md:table-cell">PATTERN</th>
            <th className="text-left p-3 font-bold text-xs hidden lg:table-cell">BODY AREA</th>
            <th className="text-left p-3 font-bold text-xs hidden lg:table-cell">EQUIPMENT</th>
            <th className="text-left p-3 font-bold text-xs">DIFFICULTY</th>
            <th className="text-right p-3 font-bold text-xs w-20">ACTIONS</th>
          </tr></thead>
          <tbody>
            {filtered.map(ex => (
              <tr key={ex.id} className="border-b border-border hover:bg-secondary/30 transition-colors">
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    {ex.thumbnail ? <img src={ex.thumbnail} alt="" className="w-10 h-10 object-cover flex-shrink-0" /> : <div className="w-10 h-10 bg-secondary flex items-center justify-center flex-shrink-0"><Dumbbell className="w-4 h-4 text-muted-foreground" /></div>}
                    <div className="min-w-0"><p className="font-medium text-sm truncate">{ex.title}</p><p className="text-xs text-muted-foreground truncate">{ex.reps && `${ex.reps} x ${ex.sets}`}{ex.duration && ` · ${ex.duration}`}</p></div>
                  </div>
                </td>
                <td className="p-3 hidden md:table-cell"><Badge variant="outline" className="text-[10px] capitalize">{ex.category}</Badge></td>
                <td className="p-3 hidden md:table-cell text-xs text-muted-foreground capitalize">{ex.movement_pattern?.replace("_", " ") || "—"}</td>
                <td className="p-3 hidden lg:table-cell text-xs text-muted-foreground capitalize">{ex.body_region?.replace("_", " ")}</td>
                <td className="p-3 hidden lg:table-cell text-xs text-muted-foreground capitalize">{ex.equipment?.replace("_", " ") || "—"}</td>
                <td className="p-3"><Badge className={ex.difficulty === "advanced" ? "bg-red-500/10 text-red-500 border-0 text-[10px]" : ex.difficulty === "intermediate" ? "bg-yellow-500/10 text-yellow-500 border-0 text-[10px]" : "bg-[#00CC52]/10 text-[#00CC52] border-0 text-[10px]"}>{ex.difficulty}</Badge></td>
                <td className="p-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button size="sm" variant="ghost" onClick={() => startEdit(ex)} className="h-7 w-7 p-0" data-testid={`ex-edit-${ex.id}`}><Edit className="w-3 h-3" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => del(ex.id)} className="h-7 w-7 p-0 text-destructive" data-testid={`ex-del-${ex.id}`}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-muted-foreground text-sm">No exercises match your filters.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Client Stories — admin manager
// Backing store: /api/stories (CRUD). Public homepage carousel reads from
// /api/stories?active_only=true.
// Design intent: keep the form to a single column of plain fields. The only
// "required" field is the quote — everything else is optional so an admin can
// post a short snippet now and flesh it out later.
// ────────────────────────────────────────────────────────────────
const EMPTY_STORY = {
  name: "",
  type: "",
  avatar: "",
  quote: "",
  challenge: "",
  revealed: "",
  changed: "",
  outcome: "",
  sort_order: 99,
  active: true,
};

function StoriesManager({ stories, clients = [], onReload }) {
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_STORY);
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();

  const openNew = () => { setForm(EMPTY_STORY); setEditId(null); setShowForm(true); };
  const openEdit = (s) => {
    setForm({
      name: s.name || "",
      type: s.type || "",
      avatar: s.avatar || "",
      quote: s.quote || "",
      challenge: s.challenge || "",
      revealed: s.revealed || "",
      changed: s.changed || "",
      outcome: s.outcome || "",
      sort_order: typeof s.sort_order === "number" ? s.sort_order : 99,
      active: s.active !== false,
    });
    setEditId(s.id);
    setShowForm(true);
  };
  const closeForm = () => { setShowForm(false); setEditId(null); setForm(EMPTY_STORY); };

  const save = async () => {
    if (!form.quote.trim()) {
      toast.error("A quote is required.");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form, sort_order: Number(form.sort_order) || 99 };
      if (editId) await api.put(`/stories/${editId}`, payload);
      else await api.post("/stories", payload);
      toast.success(editId ? "Story updated" : "Story added");
      closeForm();
      await onReload();
    } catch (e) {
      toast.error("Could not save story");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (s) => {
    try {
      await api.put(`/stories/${s.id}`, { active: !s.active });
      await onReload();
    } catch { toast.error("Could not update visibility"); }
  };

  const remove = async (s) => {
    const ok = await confirm({
      title: "Delete story?",
      description: `"${(s.quote || "").slice(0, 80)}${(s.quote || "").length > 80 ? "…" : ""}" — this can't be undone.`,
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    await api.delete(`/stories/${s.id}`);
    toast.success("Story deleted");
    await onReload();
  };

  return (
    <div className="space-y-4" data-testid="admin-stories">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-bold text-lg">Client Stories ({stories.length})</h3>
          <p className="text-xs text-muted-foreground mt-1">Shown in the homepage "Real People. Real Results." carousel.</p>
        </div>
        <Button onClick={openNew} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none gap-2" data-testid="story-add-btn">
          <Plus className="w-4 h-4" /> Add Story
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {stories.map((s) => (
          <div key={s.id} className={`bg-card border p-5 flex flex-col gap-3 ${s.active ? "border-border" : "border-border/40 opacity-60"}`} data-testid={`story-row-${s.id}`}>
            <div className="flex items-start gap-3">
              {s.avatar ? (
                <img src={s.avatar} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
              ) : (
                <div className="w-10 h-10 rounded-full bg-[#007AFF]/10 border border-[#007AFF]/30 flex items-center justify-center flex-shrink-0">
                  <span className="text-[#007AFF] font-black text-xs">{(s.name || "?").charAt(0).toUpperCase()}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate">{s.name || <span className="text-muted-foreground italic">Anonymous</span>}</p>
                {s.type && <p className="text-[10px] text-[#007AFF] font-bold uppercase tracking-wider">{s.type}</p>}
              </div>
              <span className="text-[10px] font-mono text-muted-foreground">#{s.sort_order ?? 99}</span>
            </div>
            <p className="text-sm italic text-muted-foreground line-clamp-3">"{s.quote}"</p>
            <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
              <button onClick={() => toggleActive(s)} className="text-xs font-bold tracking-wider uppercase flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors" data-testid={`story-toggle-${s.id}`}>
                {s.active ? <><Activity className="w-3 h-3 text-[#00CC52]" /> Visible</> : <><EyeOff className="w-3 h-3" /> Hidden</>}
              </button>
              <div className="flex items-center gap-1">
                <button onClick={() => openEdit(s)} className="p-2 hover:bg-secondary transition-colors" aria-label="Edit" data-testid={`story-edit-${s.id}`}>
                  <Edit className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => remove(s)} className="p-2 hover:bg-secondary hover:text-red-500 transition-colors" aria-label="Delete" data-testid={`story-delete-${s.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {stories.length === 0 && (
          <div className="lg:col-span-2 bg-card border border-dashed border-border p-12 text-center">
            <Star className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">No stories yet. Add one to start populating the homepage carousel.</p>
          </div>
        )}
      </div>

      <Dialog open={showForm} onOpenChange={(o) => { if (!o) closeForm(); }}>
        <DialogContent className="max-w-lg rounded-none bg-card border-border max-h-[90vh] overflow-y-auto" data-testid="story-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-black tracking-tight">{editId ? "Edit Story" : "Add Story"}</DialogTitle>
            <DialogDescription className="text-muted-foreground text-xs">Only the quote is required. Everything else is optional.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {!editId && clients.length > 0 && (
              <div className="bg-secondary/40 border border-border p-3" data-testid="story-prefill-from-client">
                <Label className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">Start from an existing client</Label>
                <select
                  defaultValue=""
                  onChange={(e) => {
                    const c = clients.find((x) => x.id === e.target.value);
                    if (!c) return;
                    // Map our internal client_type slug to a human-readable label for the story.
                    const typeLabel = {
                      elite_athlete:  "Elite Athlete",
                      adult_recovery: "Adult Recovery",
                      youth_athlete:  "Youth Athlete",
                    }[c.client_type] || "";
                    // First name + last initial keeps the public-facing story
                    // friendly without exposing full names.
                    const parts = (c.name || "").trim().split(/\s+/);
                    const displayName = parts.length > 1
                      ? `${parts[0]} ${parts[parts.length - 1].charAt(0)}.`
                      : parts[0] || "";
                    setForm((f) => ({
                      ...f,
                      name:   displayName || f.name,
                      type:   typeLabel    || f.type,
                      avatar: c.avatar     || f.avatar,
                    }));
                    e.target.value = "";  // allow re-picking the same client
                  }}
                  className="w-full mt-1.5 px-3 py-2 text-sm bg-background border border-border rounded-none"
                  data-testid="story-prefill-select"
                >
                  <option value="">— Pick a client to pre-fill name, photo, and type —</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.client_type ? ` · ${c.client_type.replace("_", " ")}` : ""}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-muted-foreground mt-1.5">You can still edit any field below — the picker only fills in the basics.</p>
              </div>
            )}
            <div>
              <Label className="text-xs">Quote <span className="text-red-500">*</span></Label>
              <Textarea
                value={form.quote}
                onChange={(e) => setForm({ ...form, quote: e.target.value })}
                rows={3}
                placeholder="What the client said — short, in their own words."
                className="mt-1 rounded-none bg-background border-border text-sm resize-none"
                data-testid="story-quote"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Display name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Jordan R." className="mt-1 rounded-none bg-background border-border text-sm" data-testid="story-name" />
              </div>
              <div>
                <Label className="text-xs">Type / category</Label>
                <Input value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="e.g. Elite Sprinter" className="mt-1 rounded-none bg-background border-border text-sm" data-testid="story-type" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Photo</Label>
              <AvatarUploader
                value={form.avatar}
                onChange={(dataUrl) => setForm({ ...form, avatar: dataUrl })}
                fallbackName={form.name}
              />
            </div>

            <details className="border-t border-border pt-3">
              <summary className="cursor-pointer text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground">
                Optional detail (Challenge / Assessment / Change / Outcome)
              </summary>
              <div className="space-y-3 mt-3">
                <div>
                  <Label className="text-xs">Challenge</Label>
                  <Textarea value={form.challenge} onChange={(e) => setForm({ ...form, challenge: e.target.value })} rows={2} className="mt-1 rounded-none bg-background border-border text-sm resize-none" data-testid="story-challenge" />
                </div>
                <div>
                  <Label className="text-xs">Assessment revealed</Label>
                  <Textarea value={form.revealed} onChange={(e) => setForm({ ...form, revealed: e.target.value })} rows={2} className="mt-1 rounded-none bg-background border-border text-sm resize-none" data-testid="story-revealed" />
                </div>
                <div>
                  <Label className="text-xs">What changed</Label>
                  <Textarea value={form.changed} onChange={(e) => setForm({ ...form, changed: e.target.value })} rows={2} className="mt-1 rounded-none bg-background border-border text-sm resize-none" data-testid="story-changed" />
                </div>
                <div>
                  <Label className="text-xs">Outcome</Label>
                  <Textarea value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })} rows={2} className="mt-1 rounded-none bg-background border-border text-sm resize-none" data-testid="story-outcome" />
                </div>
              </div>
            </details>

            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-border">
              <div>
                <Label className="text-xs">Sort order</Label>
                <Input type="number" min={1} value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="mt-1 rounded-none bg-background border-border text-sm" data-testid="story-sort" />
                <p className="text-[10px] text-muted-foreground mt-1">Lower = shown first.</p>
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#007AFF]" data-testid="story-active" />
                  <span>Visible on homepage</span>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={closeForm} variant="ghost" disabled={busy} className="rounded-none">Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none gap-2" data-testid="story-save-btn">
              <Save className="w-4 h-4" /> {editId ? "Save changes" : "Add story"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Audit Log — paginated, filterable feed of admin activity.
// Read from GET /api/audit-log. Read-only by design; no edit/delete.
// ────────────────────────────────────────────────────────────────
const AUDIT_ACTION_META = {
  "login.success":     { label: "Login",            tone: "text-[#00CC52] border-[#00CC52]/30 bg-[#00CC52]/10" },
  "login.failed":      { label: "Login failed",     tone: "text-red-400 border-red-400/30 bg-red-400/10" },
  "logout":            { label: "Logout",           tone: "text-muted-foreground border-border bg-muted/30" },
  "password.changed":  { label: "Password changed", tone: "text-[#007AFF] border-[#007AFF]/30 bg-[#007AFF]/10" },
  "password.reset.requested": { label: "Password reset requested", tone: "text-[#007AFF] border-[#007AFF]/30 bg-[#007AFF]/10" },
  "password.reset.completed": { label: "Password reset completed", tone: "text-[#00CC52] border-[#00CC52]/30 bg-[#00CC52]/10" },
  "client.created":    { label: "Client created",   tone: "text-[#007AFF] border-[#007AFF]/30 bg-[#007AFF]/10" },
  "client.updated":    { label: "Client updated",   tone: "text-[#007AFF] border-[#007AFF]/30 bg-[#007AFF]/10" },
  "client.deleted":    { label: "Client deleted",   tone: "text-red-400 border-red-400/30 bg-red-400/10" },
  "story.created":     { label: "Story created",    tone: "text-yellow-400 border-yellow-400/30 bg-yellow-400/10" },
  "story.updated":     { label: "Story updated",    tone: "text-yellow-400 border-yellow-400/30 bg-yellow-400/10" },
  "story.deleted":     { label: "Story deleted",    tone: "text-red-400 border-red-400/30 bg-red-400/10" },
  "lead.created":      { label: "Lead created",     tone: "text-[#00CC52] border-[#00CC52]/30 bg-[#00CC52]/10" },
  "lead.updated":      { label: "Lead updated",     tone: "text-muted-foreground border-border bg-muted/30" },
  "lead.converted":    { label: "Lead converted",   tone: "text-[#00CC52] border-[#00CC52]/30 bg-[#00CC52]/10" },
};

const AUDIT_PAGE_SIZE = 50;

function formatAuditTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

function AuditLogManager() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [actionFilter, setActionFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (nextSkip, nextAction) => {
    setLoading(true);
    try {
      const params = { limit: AUDIT_PAGE_SIZE, skip: nextSkip };
      if (nextAction) params.action = nextAction;
      const { data } = await api.get("/audit-log", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setSkip(nextSkip);
    } catch (e) {
      toast.error("Could not load activity log.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(0, ""); }, [load]);

  const onFilterChange = (next) => {
    setActionFilter(next);
    load(0, next);
  };

  const page = Math.floor(skip / AUDIT_PAGE_SIZE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / AUDIT_PAGE_SIZE));

  return (
    <div className="space-y-4" data-testid="admin-activity-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-bold text-lg">Activity Log</h3>
          <p className="text-xs text-muted-foreground">Every admin action — logins, password changes, client edits, lead conversions, story updates. Read-only.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={actionFilter}
            onChange={(e) => onFilterChange(e.target.value)}
            className="px-3 py-2 text-xs bg-background border border-border rounded-none"
            data-testid="audit-filter-action"
          >
            <option value="">All actions</option>
            {Object.entries(AUDIT_ACTION_META).map(([k, m]) => (
              <option key={k} value={k}>{m.label}</option>
            ))}
          </select>
          <Button
            onClick={() => load(0, actionFilter)}
            variant="outline"
            className="rounded-none text-xs"
            data-testid="audit-refresh-btn"
          >
            Refresh
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border">
        {loading && items.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground">Loading…</p>
        ) : items.length === 0 ? (
          <p className="p-6 text-sm text-muted-foreground" data-testid="audit-empty">No activity recorded yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((it) => {
              const meta = AUDIT_ACTION_META[it.action] || { label: it.action, tone: "text-muted-foreground border-border bg-muted/30" };
              return (
                <li key={it.id} className="px-5 py-3 flex items-start gap-4" data-testid={`audit-row-${it.id}`}>
                  <div className="w-36 flex-shrink-0 text-[11px] text-muted-foreground font-mono">
                    {formatAuditTime(it.timestamp)}
                  </div>
                  <div className="flex-shrink-0">
                    <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${meta.tone}`}>
                      {meta.label}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground truncate">{it.summary || it.action}</p>
                    <p className="text-[11px] text-muted-foreground truncate">
                      {it.actor_email || "anonymous"}
                      {it.ip ? ` · ${it.ip}` : ""}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {totalPages > 1 && (
          <div className="border-t border-border px-5 py-3 flex items-center justify-between text-xs">
            <span className="text-muted-foreground">
              Page {page} of {totalPages} · {total} total
            </span>
            <div className="flex gap-2">
              <Button
                onClick={() => load(Math.max(0, skip - AUDIT_PAGE_SIZE), actionFilter)}
                disabled={skip === 0 || loading}
                variant="outline"
                className="rounded-none text-xs"
                data-testid="audit-prev-btn"
              >
                Previous
              </Button>
              <Button
                onClick={() => load(skip + AUDIT_PAGE_SIZE, actionFilter)}
                disabled={skip + AUDIT_PAGE_SIZE >= total || loading}
                variant="outline"
                className="rounded-none text-xs"
                data-testid="audit-next-btn"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Change Password dialog (logged-in flow only).
// Hits POST /api/auth/change-password with the user's auth cookie.
// We never store the password in component state longer than needed, and we
// reset all three fields on close to avoid leaving them in memory.
// ────────────────────────────────────────────────────────────────
function ChangePasswordDialog({ open, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); setBusy(false); };
  const close = () => { reset(); onClose(); };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (next.length < 6) { toast.error("New password must be at least 6 characters."); return; }
    if (next !== confirm) { toast.error("New passwords do not match."); return; }
    if (next === current) { toast.error("New password must be different from the current one."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated.");
      close();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not change password.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-md rounded-none bg-card border-border" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-black tracking-tight flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#007AFF]" /> Change Password
          </DialogTitle>
          <DialogDescription className="text-muted-foreground text-xs">
            Pick a strong password — at least 6 characters. You'll stay signed in after the change.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs">Current password</Label>
            <Input
              type="password"
              autoComplete="current-password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="mt-1 rounded-none bg-background border-border text-sm"
              data-testid="cp-current"
              required
            />
          </div>
          <div>
            <Label className="text-xs">New password</Label>
            <Input
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              minLength={6}
              className="mt-1 rounded-none bg-background border-border text-sm"
              data-testid="cp-new"
              required
            />
          </div>
          <div>
            <Label className="text-xs">Confirm new password</Label>
            <Input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              minLength={6}
              className="mt-1 rounded-none bg-background border-border text-sm"
              data-testid="cp-confirm"
              required
            />
            {confirm && next && confirm !== next && (
              <p className="text-[11px] text-red-500 mt-1.5">Passwords don't match yet.</p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" onClick={close} variant="ghost" disabled={busy} className="rounded-none">Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-none gap-2" data-testid="cp-submit">
              <Save className="w-4 h-4" /> {busy ? "Saving…" : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


